import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Archive, ArchiveRestore, LogOut, UserPlus, X } from 'lucide-react'
import { directMessageApi } from '@/services/api/directMessages'
import type { DmConversation, DmDirectoryUser } from '@/types/direct-message'
import { engineerPublicLabel, publicRoleCaption } from '@/utils/engineerLabel'
import { EngineerBadge } from '@/components/common/EngineerBadge'

interface DmHeaderProps {
  conversation: DmConversation
  currentUserId?: string
  onClose?: () => void
  onLeft: () => void
}

export function DmHeader({ conversation, currentUserId, onClose, onLeft }: DmHeaderProps) {
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<DmDirectoryUser[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setAdding(false)
    setQ('')
    setHits([])
    setError(null)
  }, [conversation.conversation_id])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['direct-messages'] })
    queryClient.invalidateQueries({ queryKey: ['direct-messages-unread-count'] })
    queryClient.invalidateQueries({ queryKey: ['direct-message', conversation.conversation_id] })
  }

  const search = async (value: string) => {
    setQ(value)
    if (value.trim().length < 1) {
      setHits([])
      return
    }
    try {
      const res = await directMessageApi.getDirectory(value.trim())
      const existing = new Set(conversation.participants.map(p => p.user_id))
      const hasTeacher = conversation.participants.some(p => p.role === 'teacher')
      setHits(res.users.filter(u => !existing.has(u.user_id) && !(hasTeacher && u.role === 'teacher')))
    } catch {
      setHits([])
    }
  }

  const add = async (userId: string) => {
    setError(null)
    try {
      await directMessageApi.addParticipant(conversation.conversation_id, userId)
      setAdding(false)
      setQ('')
      setHits([])
      invalidate()
    } catch (e) {
      setError((e as { detail?: string }).detail || 'Hinzufügen fehlgeschlagen')
    }
  }

  const archiveToggle = async () => {
    try {
      if (conversation.is_archived) {
        await directMessageApi.unarchiveConversation(conversation.conversation_id)
      } else {
        await directMessageApi.archiveConversation(conversation.conversation_id)
      }
      invalidate()
    } catch (e) {
      setError((e as { detail?: string }).detail || 'Archivieren fehlgeschlagen')
    }
  }

  const removeUser = async (userId: string) => {
    if (!window.confirm('Teilnehmer wirklich entfernen?')) return
    setError(null)
    try {
      await directMessageApi.removeParticipant(conversation.conversation_id, userId)
      invalidate()
    } catch (e) {
      setError((e as { detail?: string }).detail || 'Entfernen fehlgeschlagen')
    }
  }

  const leave = async () => {
    if (!window.confirm('Konversation wirklich verlassen?')) return
    try {
      await directMessageApi.leave(conversation.conversation_id)
      invalidate()
      onLeft()
    } catch (e) {
      setError((e as { detail?: string }).detail || 'Verlassen fehlgeschlagen')
    }
  }

  return (
    <div className="relative h-14 bg-white border-b border-gray-200 px-4 flex items-center gap-3 shrink-0">
      {onClose && (
        <button type="button" onClick={onClose} className="md:hidden p-1 text-gray-500">
          <X className="w-4 h-4" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-gray-900 truncate">{conversation.display_title}</div>
        <div className="text-[11px] text-gray-400 truncate">
          {conversation.can_manage_participants
            ? conversation.participants.map(p => (
              <span key={p.user_id} className="mr-2 inline-flex items-center gap-1">
                {p.name}
                {p.role === 'engineer' && <EngineerBadge />}
                {p.user_id !== currentUserId && (
                <button
                  type="button"
                  onClick={() => void removeUser(p.user_id)}
                  className="text-red-400 hover:text-red-600"
                  title="Entfernen"
                >
                  ×
                </button>
                )}
              </span>
            ))
            : conversation.participants.map(p => engineerPublicLabel(p.name, p.role)).join(', ')}
        </div>
      </div>
      {conversation.can_manage_participants && (
        <button type="button" onClick={() => setAdding(v => !v)} className="p-2 text-gray-500 hover:text-gray-800" title="Teilnehmer hinzufügen">
          <UserPlus className="w-4 h-4" />
        </button>
      )}
      <button type="button" onClick={() => void archiveToggle()} className="p-2 text-gray-500 hover:text-gray-800" title={conversation.is_archived ? 'Wiederherstellen' : 'Archivieren'}>
        {conversation.is_archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
      </button>
      <button type="button" onClick={() => void leave()} className="p-2 text-gray-500 hover:text-red-600" title="Verlassen">
        <LogOut className="w-4 h-4" />
      </button>
      {adding && (
        <div className="absolute top-14 right-4 z-20 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <input
            value={q}
            onChange={e => void search(e.target.value)}
            placeholder="Name suchen…"
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          />
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          <ul className="mt-2 max-h-40 overflow-auto">
            {hits.map(u => (
              <li key={u.user_id}>
                <button type="button" onClick={() => void add(u.user_id)} className="w-full text-left px-2 py-1 text-sm hover:bg-gray-50">
                  {u.name}
                  {u.role === 'engineer' && <EngineerBadge className="ml-1" />}
                  {u.role !== 'ultraadmin' && u.role !== 'engineer' ? <span className="text-gray-400"> ({publicRoleCaption(u.role)})</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!adding && error && (
        <p className="absolute top-14 left-4 z-20 text-xs text-red-600 bg-white px-2 py-1 rounded shadow">{error}</p>
      )}
    </div>
  )
}
